import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { PiArrowsCounterClockwise, PiCalendarCheck, PiPercent, PiCurrencyEur, PiReceipt, PiPlant } from 'react-icons/pi'

import ButtonLink from '@components/common/Button/ButtonLink'
import ProgressBar from '@components/common/ProgressBar/ProgressBar'
import HeaderBack from '@/components/dashboard/Header/HeaderBack'

import MotionText from '@/components/common/Motion/MotionText'

import { useKycStore } from '@store/useKycStore'
import MotionDiv from '@/components/common/Motion/MotionDiv'

const HandbookTaxesPage = () => {
  const { t } = useTranslation()
  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  useEffect(() => setProgress(2), [])

  return (
    <div className="h-full bg-white p-5 flex flex-col">
      <HeaderBack className='items-center gap-4'>
        <ProgressBar
          currentStep={progress}
          totalSteps={4}
        />
      </HeaderBack>
      <MotionText
        as="h2"
        className="text-base-black text-base font-semibold tracking-tight mt-4 mb-2"
      >
        {t('handbook.taxes.title')}
      </MotionText>

      <MotionText
        as="p"
        className="text-zinc-700 text-sm mb-6"
      >
        {t('handbook.taxes.description')}
      </MotionText>
      
      <MotionText
        as="h3"
        className="text-base-black text-sm font-semibold tracking-tight mb-4"
      >
        {t('handbook.common.usefulToKnow')}
      </MotionText>

      <MotionDiv className='flex flex-col space-y-6 pb-24'>
        <MotionDiv className='flex gap-4'>
          <PiArrowsCounterClockwise className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.taxes.reverseVat')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiCalendarCheck className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.taxes.reverseVatDeadline')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiPercent className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.taxes.gpmDeadline')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiCurrencyEur className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.taxes.psdMonthly')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiReceipt className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.taxes.psdYearly')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiReceipt className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.taxes.vsdDeadline')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiPlant className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.taxes.pollutionTax')}
          </MotionText>
        </MotionDiv>
      </MotionDiv>

      <motion.div
        className="absolute bottom-0 -ml-5 px-5 pb-5 pt-8 w-full bg-linear-to-t from-white via-white to-transparent from-0% via-40% to-100%"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.2,
          ease: "easeOut"
        }}
      >
        <ButtonLink variant='primary' to={'/handbook/accounting'}>
          {t('common.next')}
        </ButtonLink>
      </motion.div>
    </div>
  )
}

export default HandbookTaxesPage
